import { FirebaseErrorCodes, FirebaseErrorMessages } from '@app/constants'
import { db } from '@app/firebase'
import { FirebaseError } from 'firebase/app'
import { collection, query, where, doc, getDocs, getDoc, getDocFromServer, onSnapshot, setDoc, Unsubscribe, updateDoc } from 'firebase/firestore'
import { FirestoreCollections, UserAssessment, AssessmentStatus } from '../types'

export const getUserAssessmentId = (
  userId: string,
  assessmentId: string,
  schoolYear: string,
  attempt?: number,
): string => [userId, assessmentId, schoolYear, attempt].filter(item => item).join('_')

/**
 * Create document in Firestore with initial values.
 * @param { UserAssessment } payload user assessment initial object.
 */
export const createUserAssessment = async (payload: UserAssessment) => {
  try {
    await setDoc(
      doc(
        db,
        FirestoreCollections.studentAssessments,
        `${payload.userId}_${payload.assessmentId}_${payload.schoolYear}_${payload.attempt}`,
      ),
      {
        ...payload,
      },
    )
  } catch (e) {
    console.error(e)
  }
}

export const getUserAssessments = async (userId: string, assessmentId: string) => {
  try {
    const uaCollection = collection(db, FirestoreCollections.studentAssessments)
    const q = await getDocs(query(uaCollection, where("userId", '==', userId), where("assessmentId", '==', assessmentId)))
    return q.docs.map(doc => ({ id: doc.id, ...doc.data() }) as { id: string; createdTimestamp: number, deleted: boolean })
  } catch (e) {
    console.error(e)
  }
  return []
}

/**
 * Save user assessment responses to Firestore.
 * @param { UserAssessment } payload user assessment data.
 */
export const updateUserAssessment = async (id: string, payload: Partial<UserAssessment>) => {
  try {
    await updateDoc(doc(db, FirestoreCollections.studentAssessments, id), {
      ...payload,
      type: "qti",
      updatedTimestamp: new Date(),
    })
  } catch (e) {
    console.error(e)
  }
}

/**
 * Get user assessment from Firestore by assessment id.
 * @param { String } id assessment identifier.
 * @param { Boolean } fromServer fetch from server flag. This is needed to bypass local cache in cases such as resetting an assessment.
 * @returns { Promise<UserAssessment> } user assessment Promise object.
 */
export const getUserAssessmentById = async (id: string, fromServer:boolean = false): Promise<UserAssessment> => {
  const docRef = doc(db, FirestoreCollections.studentAssessments, id)
  const docSnap = fromServer ? await getDocFromServer(docRef) : await getDoc(docRef)
  if (docSnap.exists()) {
    const userAssessment = docSnap.data() as UserAssessment
    if( userAssessment.status === AssessmentStatus.paused ) {
      userAssessment.status = AssessmentStatus.started
    }
    return userAssessment
  }
  throw new FirebaseError(FirebaseErrorCodes.NotFound, FirebaseErrorMessages.NotFound)
}

export const subscribeToUserAssessmentChanges = (
  id: string,
  onChange: (userAssessment: UserAssessment) => void,
): Unsubscribe => {
  const docRef = doc(db, FirestoreCollections.studentAssessments, id)
  const unsubscribe = onSnapshot(docRef, doc => {
    onChange(doc.data() as UserAssessment)
  })

  return unsubscribe
}
